import type { LocationConfig, LocationType } from '@/types/form.types'

/**
 * Normalise a raw locationConfig for backend submission.
 *
 * - Resolves `type` when absent (falls back to 'radius' if radiusConfig present, else 'geojson').
 * - Passes radiusConfig through unmodified for radius type (lat/lng are already honest from the store).
 * - Clears radiusConfig when type is not 'radius'.
 *
 * NOTE: There was a lat/lng swap here between 2026-01-23 and 2026-05-28 that inverted
 * radiusConfig coords to compensate for a then-broken backend. IRB PR #524 (2026-05-26)
 * fixed the backend storage order, so the swap was removed here. Do not re-introduce it.
 */
export function normalizeLocationConfig(
  rawLocationConfig: LocationConfig,
): LocationConfig {
  const resolvedType: LocationType =
    rawLocationConfig.type ??
    (rawLocationConfig.radiusConfig ? 'radius' : 'geojson')

  return {
    ...rawLocationConfig,
    type: resolvedType,
    radiusConfig: resolvedType === 'radius' ? rawLocationConfig.radiusConfig : undefined,
  }
}

/**
 * Resolve the location type from a raw config (mirrors the inline logic in serializeForBackend).
 */
export function resolveLocationType(rawLocationConfig: LocationConfig): LocationType {
  return rawLocationConfig.type ?? (rawLocationConfig.radiusConfig ? 'radius' : 'geojson')
}
